import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { apiClient, apiErrorMessage } from "../api/client";
import type { Job } from "../api/types";
import { Working } from "./Spinner";

/* A job somebody just queued, watched until it finishes.
 *
 * Three screens queue a lake.job and wait on it: Check Apollo, the buttons on
 * a campaign, and Import. Each said "queued" and then nothing moved, so a job
 * that was running and one that had failed looked the same. This is the one
 * place that asks the job how it is doing. */

const TERMINAL = ["done", "failed", "cancelled"];

export function useJob(token: string, jobId: string | null) {
  return useQuery({
    queryKey: ["job-progress", jobId],
    enabled: Boolean(jobId),
    // Every three seconds while it runs, and never once it has finished. A
    // poll that carries on after the answer is in is a request per screen per
    // three seconds for as long as somebody leaves the page open.
    refetchInterval: (q) => {
      const status = (q.state.data as Job | null)?.status;
      return status && TERMINAL.includes(status) ? false : 3000;
    },
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("job").select("id,status,error,result,finished_at,artifact_key")
        .eq("id", jobId as string).limit(1);
      if (error) throw new Error(apiErrorMessage(error));
      return ((data ?? [])[0] ?? null) as Job | null;
    },
  });
}

/* The plain lines a job printed for whoever pressed the button, which
   ingestion/job_worker.py keeps as result.summary. Empty when it printed none. */
export function jobSummary(job: Job | null | undefined): string[] {
  const summary = (job?.result as { summary?: unknown } | null)?.summary;
  return Array.isArray(summary) ? summary.filter((l): l is string => typeof l === "string") : [];
}

export function jobRunning(jobId: string | null, status: string | null | undefined) {
  return Boolean(jobId) && !TERMINAL.includes(status ?? "queued");
}

/** The line under the button: a spinner while it waits or runs, then the result. */
export function JobProgress({ token, jobId, waiting, running, done, onDone }: {
  token: string; jobId: string; waiting: string; running: string; done: string;
  onDone?: () => void;
}) {
  const job = useJob(token, jobId);
  const status = job.data?.status;
  /* The screen reloads what the job wrote, because asking somebody to press
     Refresh is asking them to do the last step by hand. */
  useEffect(() => {
    if (status === "done") onDone?.();
    // onDone is a new function on every render of the parent, and depending on
    // it would call it again each time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);
  if (job.isError) return <p className="refusal">{(job.error as Error).message}</p>;
  if (status === "done") return <p role="status">{done} <Link to="/jobs">Jobs</Link></p>;
  if (status === "failed") return <p className="refusal">Failed. {job.data?.error || "No reason recorded."}</p>;
  if (status === "cancelled") return <p className="muted">Cancelled.</p>;
  return (
    <p>
      <Working when>{status === "running" ? running : waiting}</Working>{" "}
      <Link to="/jobs">Jobs</Link>
    </p>
  );
}
